import {Point, MovingPoint, MovingArrow } from './HeroGridElements';
export default class HeroGrid {
    constructor(el) {
        this.el = el;
        this.canvas = null;
        this.ctx = null;
        this.offscreenCanvas = null;
        this.offscreenCtx = null;
        this.points = [];
        this.movingPoints = [];
        this.movingArrows = [];
        this.resizeTimeout = null;
        this.frameId = null;
        this.isDestroyed = false;
        this.skipAreaEl = document.querySelector('#hero .hero-content');

        this.lastTimestamp = 0;
        this.deltaTime = 0;

        this.config = {
            cellSize: this.getCellSize(),
            lineColor: '4, 111, 189',
            lineOpacity: 0.18,
            resizeDelay: 200,
            fps: 60,
            frameInterval: 1000 / 60,

            movingPoints: {
                count: this.getElementsCount(),
                radius: this.getMovingPointRadius(),
                color: '147, 202, 21',
                minOpacity: 1,
                maxOpacity: 1,
                minSpeed: 0.2,
                maxSpeed: 1.0,
            },

            movingArrows: {
                count: this.getElementsCount(),
                size: this.getArrowSize(),
                color: '206, 232, 255',
                minOpacity: 1,
                maxOpacity: 1,
                minSpeed: 0.2,
                maxSpeed: 1.0,
            }
        };

        this.init();
        this.startAnimation();
    }

    getElementsCount() {
        if (window.innerWidth < 1024) {
            return 3;
        } else if (window.innerWidth < 1440) {
            return 4;
        }
        return 5;
    }

    getCellSize() {
        if (window.innerWidth < 768) {
            return 30; // Mobile
        } else if (window.innerWidth < 1024) {
            return 35; // Tablet
        }
        return 40; // Desktop
    }

    getArrowSize() {
        if (window.innerWidth < 768) {
            return 12;
        } else if (window.innerWidth < 1024) {
            return 18;
        }
        return 22;
    }

    getMovingPointRadius() {
        if (window.innerWidth < 768) {
            return 4;
        } else if (window.innerWidth < 1024) {
            return 5;
        }
        return 6;
    }

    setDimensions(canvas) {
        const parent = this.canvas.parentElement;
        if (!parent) {
            console.error("No parent element found for canvas");
            return;
        }

        canvas.width = parent.offsetWidth;
        canvas.height = parent.offsetHeight;

        canvas.style.width = `${parent.offsetWidth}px`;
        canvas.style.height = `${parent.offsetHeight}px`;
    }

    createPoints(numPoints, numRows) {
        for (let i = 0; i <= numPoints; i++) {
            const row = [];
            for (let j = 0; j <= numRows; j++) {
                const x = i * this.config.cellSize;
                const y = j * this.config.cellSize;
                row.push(new Point(
                    x, y,
                ));
            }
            this.points.push(row);
        }
    }

    createMovingPoints(numPoints, numRows) {
        const { movingPoints: mp } = this.config;

        for (let i = 0; i < mp.count; i++) {
            this.movingPoints.push(
                new MovingPoint({
                    canvasWidth: this.canvas.width,
                    canvasHeight: this.canvas.height,
                    radius: mp.radius,
                    color: mp.color,
                    minOpacity: mp.minOpacity,
                    maxOpacity: mp.maxOpacity,
                    minSpeed: mp.minSpeed,
                    maxSpeed: mp.maxSpeed,
                    cellSize: this.config.cellSize,
                    numRows,
                    movingPoints: this.movingPoints
                })
            );
        }
    }

    createMovingArrows() {
        const { movingArrows: ma } = this.config;

        for (let i = 0; i < ma.count; i++) {
            this.movingArrows.push(
                new MovingArrow({
                    canvasWidth: this.canvas.width,
                    canvasHeight: this.canvas.height,
                    arrowSize: ma.size,
                    color: ma.color,
                    minOpacity: ma.minOpacity,
                    maxOpacity: ma.maxOpacity,
                    minSpeed: ma.minSpeed,
                    maxSpeed: ma.maxSpeed,
                    cellSize: this.config.cellSize,
                    movingArrows: this.movingArrows
                })
            );
        }
    }

    init() {
        this.setupCanvas();
        this.setupOffscreenCanvas();
        this.initScene();
    }

    initScene() {
        const { numPoints, numRows } = this.calculateGridDimensions();
        // get skip area rect related to canvas
        const skipAreaRect = this.skipAreaEl.getBoundingClientRect();
        const canvasRect = this.canvas.getBoundingClientRect();

        this.skipArea = {
            x: skipAreaRect.left - canvasRect.left,
            y: skipAreaRect.top - canvasRect.top,
            width: skipAreaRect.width,
            height: skipAreaRect.height
        }

        this.createPoints(numPoints, numRows);
        this.createMovingPoints(numPoints, numRows);
        this.createMovingArrows();
        this.renderStaticGrid();
    }

    setupCanvas() {
        this.canvas = this.el.querySelector('canvas');
        this.ctx = this.canvas.getContext('2d', {
            alpha: true,
            desynchronized: true // Potential performance boost on supporting browsers
        });
        this.setDimensions(this.canvas);
    }

    setupOffscreenCanvas() {
        this.offscreenCanvas = document.createElement('canvas');
        this.offscreenCtx = this.offscreenCanvas.getContext('2d', {
            alpha: true
        });
        this.setDimensions(this.offscreenCanvas);
    }

    calculateGridDimensions() {
        const numPoints = Math.ceil(this.canvas.width / this.config.cellSize);
        const numRows = Math.ceil(this.canvas.height / this.config.cellSize);
        return { numPoints, numRows };
    }

    getVisibleRange() {
        const startI = Math.max(0, Math.floor(-this.config.cellSize / this.config.cellSize));
        const endI = Math.min(
            this.points.length - 1,
            Math.ceil((this.canvas.width + this.config.cellSize) / this.config.cellSize)
        );
        const startJ = Math.max(0, Math.floor(-this.config.cellSize / this.config.cellSize));
        const endJ = Math.min(
            this.points[0].length - 1,
            Math.ceil((this.canvas.height + this.config.cellSize) / this.config.cellSize)
        );

        return { startI, endI, startJ, endJ };
    }

    renderStaticGrid() {
        const { startI, endI, startJ, endJ } = this.getVisibleRange();

        // Clear the offscreen canvas
        this.offscreenCtx.clearRect(0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);

        // Batch render lines
        this.offscreenCtx.beginPath();
        this.offscreenCtx.lineWidth = 1;
        for (let i = startI; i <= endI; i++) {
            for (let j = startJ; j <= endJ; j++) {
                const point = this.points[i][j];

                // Horizontal lines
                if (i < endI) {
                    const right = this.points[i + 1][j];
                    this.offscreenCtx.moveTo(point.x + 0.5, point.y + 0.5);
                    this.offscreenCtx.lineTo(right.x + 0.5, right.y + 0.5);
                }

                // Vertical lines
                if (j < endJ) {
                    const down = this.points[i][j + 1];
                    this.offscreenCtx.moveTo(point.x + 0.5, point.y + 0.5);
                    this.offscreenCtx.lineTo(down.x + 0.5, down.y + 0.5);
                }
            }
        }
        this.offscreenCtx.strokeStyle = `rgba(${this.config.lineColor}, ${this.config.lineOpacity})`;
        this.offscreenCtx.stroke();
    }

    draw(timestamp) {
        this.frameId = requestAnimationFrame(this.draw.bind(this));
        if (this.isDestroyed) return;

        // Calculate delta time in seconds
        if (!this.lastTimestamp) {
            this.lastTimestamp = timestamp;
        }
        this.deltaTime = (timestamp - this.lastTimestamp) / 1000; // Convert to seconds
        this.lastTimestamp = timestamp;

        // Clamp deltaTime to prevent huge jumps if tab was inactive
        this.deltaTime = Math.min(this.deltaTime, 0.1); // Max 100ms

        // Update positions based on time instead of frames
        this.movingPoints.forEach(point => {
            point.update(this.deltaTime, this.skipArea);
        });

        this.movingArrows.forEach(arrow => {
            arrow.update(this.deltaTime, this.skipArea);
        });

        // Regular drawing code
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.drawImage(this.offscreenCanvas, 0, 0);

        // Batch render moving elements
        for (const point of this.movingPoints) {
            point.draw(this.ctx);
        }

        for (const arrow of this.movingArrows) {
            arrow.draw(this.ctx, this.skipArea);
        }
    }

    startAnimation() {
        this.isDestroyed = false;
        this.frameId = requestAnimationFrame(this.draw.bind(this));
    }

    destroy() {
        this.isDestroyed = true;
        if (this.frameId) {
            cancelAnimationFrame(this.frameId);
        }
        this.points = [];
        this.movingPoints = [];
        this.movingArrows = [];
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.offscreenCtx.clearRect(0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
    }

    resize() {
        this.setDimensions(this.canvas);
        this.setDimensions(this.offscreenCanvas);
        this.destroy();
        this.initScene();
        this.startAnimation()
    }
}
